// De indeling van de wizard Nieuwe reservering, zoals Instellingen › Pension hem vastlegt: stappen met vaste blokken,
// eigen vragen en tekstblokken. De api (PensionWizardIndelingService) is de bron; de regels hier zijn dezelfde, zodat
// de beheerder niets kan verschuiven wat de api daarna weigert.
import { useCustom } from "@refinedev/core";

import { metLocatie } from "./locatie";

import type { Vertaal } from "./bouwstenen";

export type StapSoort = "ZOEKEN" | "KIEZEN" | "DIEREN" | "PERIODE" | "VERBLIJF" | "ARRANGEMENTEN" | "KEUZES" | "CONTROLE" | "EIGEN";
export type VraagType = "TEXT" | "TEXTAREA" | "NUMBER" | "BOOLEAN" | "DATE" | "SELECT" | "MULTISELECT";

export interface WizardElement {
  id: string;
  soort: "BLOK" | "VRAAG" | "TEKST";
  blok: string | null;
  titel: string | null;
  uitleg: string | null;
  actief: boolean;
  verplicht: boolean;
  type: VraagType | null;
  opties: string[] | null;
  perDier: boolean;
}

export interface WizardStap {
  id: string;
  soort: StapSoort;
  titel: string | null;
  uitleg: string | null;
  actief: boolean;
  elementen: WizardElement[];
}

export interface Indeling {
  stappen: WizardStap[];
}

/** Uit GET /api/pension/wizard-indeling/actief; versie 0 is de standaard. */
export interface ActieveIndeling {
  versie: number;
  indeling: Indeling;
}

export const VASTE_STAPPEN: StapSoort[] = ["ZOEKEN", "KIEZEN", "DIEREN", "PERIODE", "VERBLIJF", "ARRANGEMENTEN", "KEUZES", "CONTROLE"];
export const KERN: StapSoort[] = ["ZOEKEN", "KIEZEN", "DIEREN", "PERIODE", "CONTROLE"];
export const VRAAGTYPES: VraagType[] = ["TEXT", "TEXTAREA", "NUMBER", "BOOLEAN", "DATE", "SELECT", "MULTISELECT"];

export const BLOKKEN: Record<StapSoort, string[]> = {
  ZOEKEN: ["EMAIL", "ADRES"],
  KIEZEN: [],
  DIEREN: ["NIEUW_DIER"],
  PERIODE: ["LOCATIE", "BESCHIKBAARHEID"],
  VERBLIJF: ["TYPE_VERBLIJF", "TARIEF", "VOEDING", "MEDICATIE", "VERZORGING", "BIJZONDERHEDEN"],
  ARRANGEMENTEN: ["ARRANGEMENTEN"],
  KEUZES: ["VERZEKERING", "WENSEN", "NOTITIE", "INFO"],
  CONTROLE: ["SAMENVATTING", "CONCEPT", "INFO"],
  EIGEN: [],
};
export const VERPLICHTE_BLOKKEN = ["ARRANGEMENTEN", "SAMENVATTING"];

/** De standaardtekst van een blok, per stap; bij INFO is dat de tekst zelf. */
export const BLOK_TEKST: Record<string, string> = {
  "ZOEKEN.EMAIL": "nieuweReservering.zoeken.email",
  "ZOEKEN.ADRES": "nieuweReservering.zoeken.adres",
  "DIEREN.NIEUW_DIER": "nieuweReservering.dieren.nieuw",
  "PERIODE.LOCATIE": "nieuweReservering.periode.locatie",
  "PERIODE.BESCHIKBAARHEID": "nieuweReservering.periode.beschikbaarheid",
  "VERBLIJF.TYPE_VERBLIJF": "nieuweReservering.verblijf.type",
  "VERBLIJF.TARIEF": "nieuweReservering.verblijf.tarief",
  "VERBLIJF.VOEDING": "nieuweReservering.verblijf.voeding",
  "VERBLIJF.MEDICATIE": "nieuweReservering.verblijf.medicatie",
  "VERBLIJF.VERZORGING": "nieuweReservering.verblijf.verzorging",
  "VERBLIJF.BIJZONDERHEDEN": "nieuweReservering.verblijf.bijzonderheden",
  "ARRANGEMENTEN.ARRANGEMENTEN": "nieuweReservering.arrangementen.titel",
  "KEUZES.VERZEKERING": "nieuweReservering.keuzes.verzekering",
  "KEUZES.WENSEN": "nieuweReservering.keuzes.wensen",
  "KEUZES.NOTITIE": "nieuweReservering.keuzes.notitie",
  "KEUZES.INFO": "nieuweReservering.keuzes.info",
  "CONTROLE.SAMENVATTING": "nieuweReservering.som.titel",
  "CONTROLE.CONCEPT": "nieuweReservering.controle.concept",
  "CONTROLE.INFO": "nieuweReservering.controle.info",
};

/** De standaarduitleg onder een blok, waar de wizard die heeft. */
export const BLOK_UITLEG: Record<string, string> = {
  "KEUZES.VERZEKERING": "nieuweReservering.keuzes.verzekeringUitleg",
  "KEUZES.WENSEN": "nieuweReservering.keuzes.wensenUitleg",
  "KEUZES.NOTITIE": "nieuweReservering.keuzes.notitieUitleg",
};

function blokId(stap: StapSoort, blok: string): string {
  return (blok === "INFO" ? `${stap}-${blok}` : blok).toLowerCase().replace(/_/g, "-");
}

/** De wizard zoals hij gebouwd is; gelijk aan PensionWizardIndelingService.standaard(). */
export function standaardIndeling(): Indeling {
  return {
    stappen: VASTE_STAPPEN.map((soort) => ({
      id: soort.toLowerCase(),
      soort,
      titel: null,
      uitleg: null,
      actief: true,
      elementen: BLOKKEN[soort].map((blok) => ({
        id: blokId(soort, blok),
        soort: "BLOK" as const,
        blok,
        titel: null,
        uitleg: null,
        actief: true,
        verplicht: false,
        type: null,
        opties: null,
        perDier: false,
      })),
    })),
  };
}

/**
 * De indeling waarmee de wizard werkt; zolang de api niets zegt, de standaard. De indeling hoort bij een vestiging:
 * kiest de klant een andere locatie, dan hoort daar ook de indeling van dat pension bij.
 */
export function useActieveIndeling(vestigingId: number | null = null): ActieveIndeling {
  const data = useCustom<ActieveIndeling>({
    url: metLocatie("pension/wizard-indeling/actief", vestigingId), method: "get",
  }).data?.data;
  return data && Array.isArray(data.indeling?.stappen) ? data : { versie: 0, indeling: standaardIndeling() };
}

// ── Teksten ──────────────────────────────────────────────────────────────────────────────────────────

/** De naam van een stap in de stappenbalk. */
export function stapNaam(t: Vertaal, s: WizardStap): string {
  return s.titel ?? (s.soort === "EIGEN" ? "" : t(`nieuweReservering.stap.${s.soort.toLowerCase()}`));
}

/** De kop en uitleg bovenaan een stap: de eigen tekst, anders die van de wizard. */
export function stapKop(t: Vertaal, s: WizardStap | undefined, sleutel: string): { titel: string; uitleg: string } {
  return {
    titel: s?.titel ?? t(`nieuweReservering.${sleutel}.titel`),
    uitleg: s?.uitleg ?? t(`nieuweReservering.${sleutel}.uitleg`),
  };
}

export function blokTitel(t: Vertaal, stap: StapSoort, e: WizardElement): string {
  if (e.titel) return e.titel;
  const sleutel = BLOK_TEKST[`${stap}.${e.blok}`];
  return e.blok === "INFO" ? "" : sleutel ? t(sleutel) : (e.blok ?? "");
}

export function blokUitleg(t: Vertaal, stap: StapSoort, e: WizardElement): string {
  if (e.uitleg) return e.uitleg;
  const sleutel = e.blok === "INFO" ? BLOK_TEKST[`${stap}.INFO`] : BLOK_UITLEG[`${stap}.${e.blok}`];
  return sleutel ? t(sleutel) : "";
}

// ── Lezen in de wizard ───────────────────────────────────────────────────────────────────────────────

export function stapVan(indeling: Indeling, soort: StapSoort): WizardStap | undefined {
  return indeling.stappen.find((s) => s.soort === soort);
}

/** Een blok staat aan als de stap het heeft en het niet uit staat; zonder indeling staat alles aan. */
export function blokAan(stap: WizardStap | undefined, blok: string): boolean {
  if (!stap) return true;
  const e = stap.elementen.find((x) => x.soort === "BLOK" && x.blok === blok);
  return e ? e.actief : false;
}

export function blokElement(stap: WizardStap | undefined, blok: string): WizardElement | undefined {
  return stap?.elementen.find((x) => x.soort === "BLOK" && x.blok === blok);
}

/** De eigen vragen en teksten van een stap die meedoen, in volgorde. */
export function eigenElementen(stap: WizardStap | undefined): WizardElement[] {
  return (stap?.elementen ?? []).filter((e) => e.soort !== "BLOK" && e.actief);
}

/** Wat voor en na een vast blok staat, voor stappen die om één groot blok heen gebouwd zijn. */
export function rondBlok(stap: WizardStap | undefined, blok: string): { voor: WizardElement[]; na: WizardElement[] } {
  const elementen = stap?.elementen ?? [];
  const plek = elementen.findIndex((e) => e.soort === "BLOK" && e.blok === blok);
  const eigen = (lijst: WizardElement[]) => lijst.filter((e) => e.soort !== "BLOK" && e.actief);
  return plek < 0 ? { voor: [], na: eigen(elementen) } : { voor: eigen(elementen.slice(0, plek)), na: eigen(elementen.slice(plek + 1)) };
}

// ── Antwoorden ───────────────────────────────────────────────────────────────────────────────────────

/** Per vraag ("gewicht") of per vraag en dier ("gewicht:12") het antwoord als tekst. */
export type Antwoorden = Record<string, string>;

export const antwoordSleutel = (elementId: string, dierId?: number | null) => (dierId == null ? elementId : `${elementId}:${dierId}`);

function beantwoord(waarde: string | undefined): boolean {
  if (waarde == null || waarde.trim() === "") return false;
  return waarde !== "[]";
}

/** De verplichte vragen van een stap die nog open staan. */
export function openVragen(stap: WizardStap | undefined, antwoorden: Antwoorden, dierIds: number[]): WizardElement[] {
  return eigenElementen(stap).filter(
    (e) =>
      e.soort === "VRAAG" &&
      e.verplicht &&
      (e.perDier ? dierIds.some((id) => !beantwoord(antwoorden[antwoordSleutel(e.id, id)])) : !beantwoord(antwoorden[e.id])),
  );
}

/** De antwoorden voor de api: alleen vragen die meedoen, per dier alleen voor de gekozen dieren. */
export function antwoordenVoorApi(indeling: Indeling, antwoorden: Antwoorden, dierIds: number[]) {
  const uit: { elementId: string; dierId: number | null; waarde: string }[] = [];
  for (const stap of indeling.stappen) {
    if (!stap.actief) continue;
    for (const e of eigenElementen(stap)) {
      if (e.soort !== "VRAAG") continue;
      for (const dierId of e.perDier ? dierIds : [null]) {
        const waarde = antwoorden[antwoordSleutel(e.id, dierId)];
        if (beantwoord(waarde)) uit.push({ elementId: e.id, dierId, waarde: waarde.trim() });
      }
    }
  }
  return uit;
}

// ── Regels voor het indelen ──────────────────────────────────────────────────────────────────────────

/** Wat er aan de volgorde en de stappen niet klopt, als sleutel van een melding; null als alles klopt. */
export function volgordeFout(stappen: WizardStap[]): string | null {
  const plek = (soort: StapSoort) => stappen.findIndex((s) => s.soort === soort);
  if (plek("ZOEKEN") !== 0 || plek("KIEZEN") !== 1) return "klantEerst";
  if (plek("CONTROLE") !== stappen.length - 1) return "controleLaatst";
  if (plek("DIEREN") > plek("PERIODE")) return "dierenVoorPeriode";
  if ((["VERBLIJF", "ARRANGEMENTEN", "KEUZES"] as StapSoort[]).some((s) => plek(s) < plek("PERIODE"))) return "naPeriode";
  const dieren = plek("DIEREN");
  if (stappen.some((s, i) => i <= dieren && s.elementen.some((e) => e.soort === "VRAAG" && e.perDier))) return "perDierNaDieren";
  return null;
}

/** Wat er in de indeling niet klopt, als sleutel van een melding; null als hij bewaard kan worden. */
export function indelingFout(indeling: Indeling): string | null {
  const volgorde = volgordeFout(indeling.stappen);
  if (volgorde) return volgorde;
  for (const s of indeling.stappen) {
    if (s.soort === "EIGEN" && !s.titel?.trim()) return "eigenStapNaam";
    if (s.soort === "EIGEN" && s.elementen.length === 0) return "eigenStapLeeg";
    if (s.soort === "ZOEKEN" && !s.elementen.some((e) => e.soort === "BLOK" && e.actief && (e.blok === "EMAIL" || e.blok === "ADRES"))) {
      return "zoekenLeeg";
    }
    for (const e of s.elementen) {
      if (e.soort === "VRAAG" && !e.titel?.trim()) return "vraagTekst";
      if (e.soort === "VRAAG" && (e.type === "SELECT" || e.type === "MULTISELECT") && (e.opties ?? []).filter((o) => o.trim()).length === 0) {
        return "vraagOpties";
      }
      if (e.soort === "TEKST" && !e.titel?.trim() && !e.uitleg?.trim()) return "tekstLeeg";
    }
  }
  return null;
}

/** Of een stap van plek {@code van} naar plek {@code naar} kan zonder de regels te breken. */
export function magStapVerplaatsen(stappen: WizardStap[], van: number, naar: number): boolean {
  if (naar < 0 || naar >= stappen.length || van === naar) return false;
  const kopie = [...stappen];
  const [stap] = kopie.splice(van, 1);
  kopie.splice(naar, 0, stap);
  return volgordeFout(kopie) == null;
}

/** Een nieuwe, nog niet gebruikte sleutel voor een stap of element. */
export function nieuweId(indeling: Indeling, basis: string): string {
  const gebruikt = new Set(indeling.stappen.flatMap((s) => [s.id, ...s.elementen.map((e) => e.id)]));
  const schoon =
    basis
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 30) || "vraag";
  let id = schoon;
  for (let n = 2; gebruikt.has(id); n++) id = `${schoon}-${n}`;
  return id;
}
